import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useRouteContext } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { Button, EmptyState, ErrorState } from '@bb/ui';
import { ApiError, type EventStatus } from '../api/client.ts';
import { environmentsQuery } from '../api/queries.ts';
import { Can } from '../features/access/Can.tsx';

export const EVENT_STATUS_LABEL: Record<EventStatus, string> = { unreviewed: 'Unreviewed', active: 'Active', deprecated: 'Deprecated', blocked: 'Blocked', test_only: 'Test only' };
const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');
const shortDay = (iso: string | null) => (iso ? iso.slice(0, 10) : '—');

/** Data → Custom events (BB-27): the registry with descriptions, statuses, volume and the unreviewed backlog. */
export function EventsPage() {
  const { environmentId } = useParams({ from: '/env/$environmentId/data/events' });
  const { api } = useRouteContext({ from: '/env/$environmentId/data/events' });
  const queryClient = useQueryClient();
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<'' | EventStatus>('');
  const events = useQuery({ queryKey: ['events', environmentId, query, status], queryFn: () => api.listEvents(environmentId, { query, status }) });
  const { data: environments = [] } = useQuery(environmentsQuery(api));
  const production = environments.find((e) => e.id === environmentId)?.isProduction ?? false;
  const [name, setName] = useState('');
  const create = useMutation({
    mutationFn: () => api.createEvent(environmentId, { name: name.trim() }),
    onSuccess: () => {
      setName('');
      void queryClient.invalidateQueries({ queryKey: ['events', environmentId] });
    },
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (name.trim()) create.mutate();
  };

  return (
    <section>
      <h1>Custom events</h1>
      {events.data && events.data.unreviewed > 0 && (
        <p role={production ? 'alert' : 'status'} className={production ? 'notice notice--warning' : 'notice notice--neutral'}>
          {events.data.unreviewed} unreviewed events{production ? ' arrived in production' : ''}: describe them or mark them test-only or blocked.
        </p>
      )}
      <div className="filters">
        <label>
          Search <input aria-label="Search events" value={query} onChange={(e) => setQuery(e.target.value)} />
        </label>
        <label>
          Status{' '}
          <select aria-label="Filter by status" value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
            <option value="">All statuses</option>
            {Object.entries(EVENT_STATUS_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <a className="button" href={`/admin/v1/environments/${environmentId}/catalog/events/export.csv`} download>
          Export CSV
        </a>
      </div>
      {events.isPending ? (
        <p aria-busy="true">Loading events…</p>
      ) : events.isError ? (
        <ErrorState title="Events could not be loaded" message={errorText(events.error)} onRetry={() => void events.refetch()} />
      ) : events.data.items.length === 0 ? (
        <EmptyState title="No events yet" description={query || status ? 'Nothing matches the filters.' : 'Send one from the sample app, or add an event before the app ships it.'} />
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Description</th>
              <th scope="col">Status</th>
              <th scope="col">Producer</th>
              <th scope="col">Owner</th>
              <th scope="col">Volume 30d</th>
              <th scope="col">Last seen</th>
            </tr>
          </thead>
          <tbody>
            {events.data.items.map((e) => (
              <tr key={e.name}>
                <td>
                  <Link to="/env/$environmentId/data/events/$name" params={{ environmentId, name: e.name }}>
                    {e.name}
                  </Link>
                </td>
                <td>{e.description ?? <span className="hint">—</span>}</td>
                <td>
                  <span className={e.status === 'blocked' || e.status === 'unreviewed' ? 'tag tag--warning' : 'tag tag--neutral'}>{EVENT_STATUS_LABEL[e.status]}</span>
                </td>
                <td>{e.producer}</td>
                <td>{e.owner ?? '—'}</td>
                <td>{e.volume30d}</td>
                <td>{shortDay(e.lastSeenAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <Can permission="catalog.edit">
        <form className="stack-form" aria-label="Add event" onSubmit={submit}>
          <label>
            Event name
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="deposit_bonus_granted" />
          </label>
          <Button type="submit" disabled={create.isPending || !name.trim()}>
            Add event
          </Button>
          {create.isError && <span role="alert">{errorText(create.error)}</span>}
        </form>
      </Can>
    </section>
  );
}
